import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import type { Component } from 'vue';
import {
  ArrowLeftRight,
  BarChart3,
  Clock,
  Eye,
  Layers,
  Maximize,
  Monitor,
  Mouse,
  MousePointerClick,
  Play,
  RefreshCw,
  RotateCcw,
  Scaling,
  Search,
  Settings,
  Star,
  Tag,
  ZoomIn,
  Image,
  Minus,
} from 'lucide-vue-next';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  SETTINGS_TOOLBAR_TOP,
  SETTINGS_TOOLBAR_BOTTOM,
  SETTINGS_TOOLBAR_SETTINGS_BAR,
  SETTINGS_TOP_BAR_VIS,
  SETTINGS_BOTTOM_BAR_VIS,
  SETTINGS_TOP_BAR_STYLE,
  SETTINGS_BOTTOM_BAR_STYLE,
  SETTINGS_SETTINGS_BAR_STYLE,
} from '@/stores/localStorage';

export type BarSlot = 'top' | 'bottom' | 'settings-bar';
export type ViewerMode = 'single' | 'desktop' | 'mobile';
export type BarStyle = 'icon-only' | 'label-only' | 'icon+label';

export type ToolbarItemId =
  | 'prev-next'
  | 'page'
  | 'search'
  | 'refresh'
  | 'tag-overlay'
  | 'vote-overlay'
  | 'meta-overlay'
  | 'autoplay'
  | 'image-size'
  | 'zoom'
  | 'fullscreen'
  | 'page-reload'
  | 'preload-stats'
  | 'fetch-stats'
  | 'stats'
  | 'viewer-mode'
  | 'mouse-votes'
  | 'settings'
  | 'spacer';

/** A single placed instance of a toolbar item in a bar. */
export interface BarItemEntry {
  id: ToolbarItemId;
  /** Unique instance key (UUID) so the same item can appear multiple times. */
  key: string;
}

export type BarVisibility = 'always' | 'autohide' | 'hover' | 'fixed';

export function resolveBarVisibility(
  viewerMode: ViewerMode,
  visibility: BarVisibility,
): BarVisibility {
  if (viewerMode === 'single' && (visibility === 'always' || visibility === 'autohide')) {
    return 'fixed';
  }

  // "Show on scroll" (autohide) only makes sense in mobile vertical-scroll mode;
  // disable it for desktop and mobile by falling back to fixed.
  if ((viewerMode === 'desktop' || viewerMode === 'mobile') && visibility === 'autohide') {
    return 'fixed';
  }

  return visibility;
}

export function isBarVisibilityDisabled(
  viewerMode: ViewerMode,
  visibility: BarVisibility,
): boolean {
  return (viewerMode === 'desktop' || viewerMode === 'mobile') && visibility === 'autohide';
}

/** All toolbar item IDs in the default reservoir order. */
export const ALL_TOOLBAR_ITEM_IDS: ToolbarItemId[] = ['prev-next', 'page', 'search', 'refresh', 'tag-overlay', 'vote-overlay', 'meta-overlay', 'autoplay', 'image-size', 'zoom', 'fullscreen', 'page-reload', 'preload-stats', 'fetch-stats', 'stats', 'viewer-mode', 'mouse-votes', 'settings', 'spacer'];

const DEFAULT_TOP: BarItemEntry[] = [];

const DEFAULT_SETTINGS_BAR: BarItemEntry[] = [
{
 id: 'prev-next',
key: 'default-settings-bar-prev-next',
},
{
 id: 'refresh',
key: 'default-settings-bar-refresh',
},
{
 id: 'tag-overlay',
key: 'default-settings-bar-tag-overlay',
},
{
 id: 'vote-overlay',
key: 'default-settings-bar-vote-overlay',
},
];

const DEFAULT_BOTTOM: BarItemEntry[] = [
{
 id: 'prev-next',
key: 'default-prev-next',
},
{
 id: 'page',
key: 'default-page',
},
{
 id: 'search',
key: 'default-search',
},
{
 id: 'tag-overlay',
key: 'default-tag-overlay',
},
{
 id: 'vote-overlay',
key: 'default-vote-overlay',
},
{
 id: 'settings',
key: 'default-settings',
},
];

/** Representative icon component for each toolbar item (used in config panel) */
export const TOOLBAR_ITEM_ICONS: Record<ToolbarItemId, Component> = {
  'prev-next': ArrowLeftRight,
  page: Layers,
  search: Search,
  refresh: RefreshCw,
  'tag-overlay': Tag,
  'vote-overlay': Star,
  'meta-overlay': Eye,
  autoplay: Play,
  'image-size': Scaling,
  zoom: ZoomIn,
  fullscreen: Maximize,
  'page-reload': RotateCcw,
  'preload-stats': Image,
  'fetch-stats': Clock,
  stats: BarChart3,
  'viewer-mode': Monitor,
  'mouse-votes': MousePointerClick,
  settings: Settings,
  spacer: Minus,
};

/** Human-readable label for each toolbar item */
export const TOOLBAR_ITEM_LABELS: Record<ToolbarItemId, string> = {
  'prev-next': 'Prev / Next',
  page: 'Page selector',
  search: 'Search',
  refresh: 'Refresh',
  'tag-overlay': 'Tag overlay',
  'vote-overlay': 'Vote overlay',
  'meta-overlay': 'Meta overlay',
  autoplay: 'Autoplay',
  'image-size': 'Image size mode',
  zoom: 'Zoom',
  fullscreen: 'Fullscreen',
  'page-reload': 'Reload page',
  'preload-stats': 'Preload stats',
  'fetch-stats': 'Fetch stats',
  stats: 'Stats panel',
  'viewer-mode': 'Viewer mode',
  'mouse-votes': 'Mouse votes',
  settings: 'Settings',
  spacer: 'Spacer',
};

export const useToolbarStore = defineStore('toolbar', () => {
  const topItems = ref<BarItemEntry[]>(
    getLocalStorageItem(SETTINGS_TOOLBAR_TOP, DEFAULT_TOP),
  );
  const bottomItems = ref<BarItemEntry[]>(
    getLocalStorageItem(SETTINGS_TOOLBAR_BOTTOM, DEFAULT_BOTTOM),
  );
  const settingsBarItems = ref<BarItemEntry[]>(
    getLocalStorageItem(SETTINGS_TOOLBAR_SETTINGS_BAR, DEFAULT_SETTINGS_BAR),
  );

  watch(topItems, (v) => setLocalStorageItem(SETTINGS_TOOLBAR_TOP, v), { deep: true });
  watch(bottomItems, (v) => setLocalStorageItem(SETTINGS_TOOLBAR_BOTTOM, v), { deep: true });
  watch(settingsBarItems, (v) => setLocalStorageItem(SETTINGS_TOOLBAR_SETTINGS_BAR, v), { deep: true });

  /** Add a new instance of an item to the given bar slot. */
  function addItem(id: ToolbarItemId, slot: BarSlot): BarItemEntry {
    const entry: BarItemEntry = {
 id,
key: crypto.randomUUID(),
};
    if (slot === 'top') topItems.value.push(entry);
    else if (slot === 'bottom') bottomItems.value.push(entry);
    else settingsBarItems.value.push(entry);
    return entry;
  }

  /** Remove a placed instance by its unique key. */
  function removeItem(key: string) {
    topItems.value = topItems.value.filter((i) => i.key !== key);
    bottomItems.value = bottomItems.value.filter((i) => i.key !== key);
    settingsBarItems.value = settingsBarItems.value.filter((i) => i.key !== key);
  }

  /** Replace the ordered list for a slot with the given entries. */
  function reorder(slot: BarSlot, entries: BarItemEntry[]) {
    if (slot === 'top') topItems.value = entries;
    else if (slot === 'bottom') bottomItems.value = entries;
    else settingsBarItems.value = entries;
  }

  function resetToDefault() {
    topItems.value = DEFAULT_TOP.map((i) => ({ ...i }));
    bottomItems.value = DEFAULT_BOTTOM.map((i) => ({ ...i }));
    settingsBarItems.value = DEFAULT_SETTINGS_BAR.map((i) => ({ ...i }));
  }

  /** Whether any bar currently contains a given item id. */
  function hasItemInAnyBar(id: ToolbarItemId): boolean {
    return (
      topItems.value.some((i) => i.id === id) ||
      bottomItems.value.some((i) => i.id === id)
    );
  }

  const topBarVisibility = ref<BarVisibility>(
    getLocalStorageItem(SETTINGS_TOP_BAR_VIS, 'always'),
  );
  watch(topBarVisibility, (v) => setLocalStorageItem(SETTINGS_TOP_BAR_VIS, v));

  const bottomBarVisibility = ref<BarVisibility>(
    getLocalStorageItem(SETTINGS_BOTTOM_BAR_VIS, 'always'),
  );
  watch(bottomBarVisibility, (v) => setLocalStorageItem(SETTINGS_BOTTOM_BAR_VIS, v));

  const topBarStyle = ref<BarStyle>(
    getLocalStorageItem(SETTINGS_TOP_BAR_STYLE, 'icon-only'),
  );
  watch(topBarStyle, (v) => setLocalStorageItem(SETTINGS_TOP_BAR_STYLE, v));

  const bottomBarStyle = ref<BarStyle>(
    getLocalStorageItem(SETTINGS_BOTTOM_BAR_STYLE, 'icon-only'),
  );
  watch(bottomBarStyle, (v) => setLocalStorageItem(SETTINGS_BOTTOM_BAR_STYLE, v));

  const settingsBarStyle = ref<BarStyle>(
    getLocalStorageItem(SETTINGS_SETTINGS_BAR_STYLE, 'icon-only'),
  );
  watch(settingsBarStyle, (v) => setLocalStorageItem(SETTINGS_SETTINGS_BAR_STYLE, v));

  return {
    topItems,
    bottomItems,
    settingsBarItems,
    addItem,
    removeItem,
    reorder,
    resetToDefault,
    hasItemInAnyBar,
    topBarVisibility,
    bottomBarVisibility,
    topBarStyle,
    bottomBarStyle,
    settingsBarStyle,
  };
});
